import { useCallback, useContext, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import {
  Button,
  Input,
  InputMask,
  RadioButton,
  Paragraph,
  Hidden
} from 'applaus-ui-kit'
import { useFormik } from 'formik'
import { history } from '../../routes/services/history'
import UnauthenticatedTemplate from '../../components/unauthenticatedTemplate/Unauthenticated'
import * as S from './CreatorSignup.styles'
import {
  validationSchemaCompany,
  validationSchemaPersonal
} from './validationSchema'

import { IconBack, Title} from '../../components/buttonBack/ButtonBack.styles'

import TemplateImage from '../../assets/templateImage.png'
import { ButtonBack } from '../../components/buttonBack/ButtonBack'
import { registerCreatorService } from '../../api/services/creator/registerCreator'
import { AuthContext } from '../../contexts/AuthContext'
import Logo from '../../assets/applaus-logo-white.png'
import { useWindowSize } from '../../hooks/useWindowSize'
import jsonStates from '../../utils/estados-cidades.json'
import ReactSelect from 'react-select'
import { getCepService } from '../../api/services/getCepService'
import { getInterestsService, Interest } from '../../api/services/interests/getInterestsService'
import { useQuery } from '../../hooks/useQuery'
import { getPartnerInviteService } from '../../api/services/partnerInvite/getPartnerInviteService'

type SocialNetwork = {
  type: string;
  url: string;
}

type SelectOption = { value: string; label: string }

const GENDER_OPTIONS: SelectOption[] = [
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'FEMININO', label: 'Feminino' },
  { value: 'OUTRO', label: 'Outro' },
  { value: 'PREFIRO_NAO_INFORMAR', label: 'Prefiro não informar' }
]

const SOCIAL_NETWORK_OPTIONS: SelectOption[] = [
  { value: 'Instagram', label: 'Instagram' },
  { value: 'X', label: 'X' },
  { value: 'Spotify', label: 'Spotify' },
  { value: 'Youtube', label: 'Youtube' }
]

const noOptionsState = () => 'Estado não encontrado'
const noOptionsGender = () => 'Opção não encontrada'

export const CreatorSignup = () => {
  const query = useQuery()
  const inviteToken = query.get('inviteToken')
  const { setLoading, setModalState } = useContext(AuthContext)
  const [isCompany, setIsCompany] = useState(false)
  const [inviteInfo, setInviteInfo] = useState<{ eventName: string; inviterName: string } | null>(null)
  const { isMobile } = useWindowSize()
  const [states] = useState<{ value: string; label: string }[]>(
    jsonStates.estados.map((state) => ({
      value: state.sigla,
      label: state.nome
    }))
  )
  const [cities, setCities] = useState<{ value: string; label: string }[]>([])
  const [currentStep, setCurrentStep] = useState(1)

  const [selectedState, setSelectedState] = useState('')

  const [profileImage, setProfileImage] = useState<File | null>(null)
  const [profileImagePreview, setProfileImagePreview] = useState<string>('')
  const [socialNetworks, setSocialNetworks] = useState<SocialNetwork[]>([
    { type: 'Instagram', url: '' }
  ])

  const [selectedInterests, setSelectedInterests] = useState<string[]>([])
  const [interests, setInterests] = useState<Interest[]>([])

  // Busca os interesses da API
  useEffect(() => {
    const fetchInterests = async () => {
      try {
        const data = await getInterestsService()
        setInterests(data)
      } catch (error) {
        console.error('Erro ao buscar interesses:', error)
      }
    }

    fetchInterests()
  }, [])

  useEffect(() => {
    if (selectedState !== '') {
      setCities(
        jsonStates.estados
          .filter((state) => state.sigla === selectedState)[0]
          .cidades.map((city) => ({
            value: city,
            label: city
          }))
      )
    }
  }, [selectedState])

  const resetAlertModal = () => {
    setModalState({
      message: '',
      open: false,
      variant: 'error'
    })
  }

  const handleAddSocialNetwork = () => {
    setSocialNetworks([...socialNetworks, { type: 'Instagram', url: '' }])
  }

  const handleSocialNetworkChange = (index: number, field: 'type' | 'url', value: string) => {
    const newNetworks = [...socialNetworks]
    newNetworks[index][field] = value
    setSocialNetworks(newNetworks)
  }

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setProfileImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setProfileImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleInterestToggle = (interestId: string) => {
    setSelectedInterests(prev =>
      prev.includes(interestId)
        ? prev.filter(i => i !== interestId)
        : [...prev, interestId]
    )
  }

  const validationSchema = useMemo(
    () => (isCompany ? validationSchemaCompany : validationSchemaPersonal),
    [isCompany]
  )

  const formik = useFormik({
    initialValues: {
      name: '',
      lastName: '',
      corporateName: '',
      document: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      city: '',
      state: '',
      district: '',
      zipCode: '',
      street: '',
      number: '',
      description: '',
      gender: '',
      complement: '',
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      try {
        setLoading(true)
        formik.setErrors({})
        await registerCreatorService({
          firstName: values.name,
          lastName: isCompany ? values.name : values.lastName,
          document: values.document,
          email: values.email,
          documentType: isCompany ? 'CNPJ' : 'CPF',
          password: values.password,
          phone: values.phone,
          corporateName:
            values.corporateName !== '' ? values.corporateName : undefined,
          description: values.description,
          image: profileImage || undefined,
          gender: !isCompany ? values.gender : undefined,
          socialNetworks: socialNetworks.filter(network => network.url.trim() !== ''),
          categories: selectedInterests,
          address: {
            city: values.city,
            state: values.state,
            country: 'BR',
            district: values.district,
            number: Number(values.number),
            street: values.street,
            zipCode: values.zipCode,
            complement: values.complement || ''
          },
          partnerInviteToken: inviteToken ?? undefined
        })
        formik.resetForm()
        setLoading(false)
        setModalState({
          message: 'Parabéns! Cadastro feito com sucesso.',
          open: true,
          variant: 'success',
          click: () => {
            setModalState({
              message: '',
              open: false,
              variant: 'error'
            })
            history.push('/')
          }
        })
      } catch (err: any) {
        setLoading(false)
        setModalState({
          open: true,
          variant: 'error',
          message: 'Ocorreu algum erro, tente novamente mais tarde.',
          buttonText: 'Fechar',
          click: () => resetAlertModal()
        })
      }
    }
  })

  const handlePersonTypeChange = (company: boolean) => {
    if (company === isCompany) return
    setIsCompany(company)
    formik.setFieldValue('document', '', false)
    formik.setFieldValue('corporateName', '', false)
    formik.setFieldValue('gender', '', false)
    formik.setErrors({})
  }

  useEffect(() => {
    const navPath = sessionStorage.getItem('applaus-signup-nav')
    sessionStorage.removeItem('applaus-signup-nav')
    if (navPath !== '/creatorsignup') return

    const blockGhostClick = (event: Event) => {
      event.stopPropagation()
      event.preventDefault()
    }
    document.addEventListener('click', blockGhostClick, true)
    const timer = window.setTimeout(() => {
      document.removeEventListener('click', blockGhostClick, true)
    }, 400)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('click', blockGhostClick, true)
    }
  }, [])

  const stateSelectValue = useMemo(
    () => states.find((obj) => formik.values.state === obj.value) ?? null,
    [states, formik.values.state]
  )

  const citySelectValue = useMemo(
    () => cities.find((obj) => formik.values.city === obj.value) ?? null,
    [cities, formik.values.city]
  )

  const genderSelectValue = useMemo(
    () => GENDER_OPTIONS.find((obj) => formik.values.gender === obj.value) ?? null,
    [formik.values.gender]
  )

  const cityNoOptionsMessage = useCallback(
    () => (selectedState ? 'Cidade não encontrada' : 'Selecione o estado'),
    [selectedState]
  )

  const handleStateChange = useCallback(
    (option: SelectOption | null) => {
      const newValue = option?.value ?? ''
      if (newValue === formik.values.state) return
      setSelectedState(newValue)
      formik.setFieldValue('state', newValue, false)
      formik.setFieldValue('city', '', false)
    },
    [formik.setFieldValue, formik.values.state]
  )

  const handleCityChange = useCallback(
    (option: SelectOption | null) => {
      const newValue = option?.value ?? ''
      if (newValue === formik.values.city) return
      formik.setFieldValue('city', newValue, false)
    },
    [formik.setFieldValue, formik.values.city]
  )

  const handleGenderChange = useCallback(
    (option: SelectOption | null) => {
      const newValue = option?.value ?? ''
      if (newValue === formik.values.gender) return
      formik.setFieldValue('gender', newValue, false)
    },
    [formik.setFieldValue, formik.values.gender]
  )

  useEffect(() => {
    if (!inviteToken) return

    const loadInvite = async () => {
      try {
        const invite = await getPartnerInviteService(inviteToken)
        setInviteInfo({ eventName: invite.eventName, inviterName: invite.inviterName })
        formik.setFieldValue('email', invite.email)
      } catch {
        setModalState({
          open: true,
          variant: 'error',
          message: 'Convite inválido ou expirado.',
          buttonText: 'Fechar',
          click: () => history.push('/register')
        })
      }
    }

    loadInvite()
  }, [inviteToken])

  const handleCep = async (e: string) => {
    if (e.replace('-', '').replace(' ', '').length > 7) {
      const response: any = await getCepService(e)
      if (response.erro === undefined) {
        setSelectedState(response.uf.toUpperCase())
        formik.setFieldValue('street', response.logradouro)
        formik.setFieldValue('state', response.uf.toUpperCase())
        formik.setFieldValue('city', response.localidade)
        formik.setFieldValue('district', response.bairro)
      }
    }
  }

  const handleNextStep = async () => {
    if (currentStep === 1) {
      const firstStepFields = [
        'name',
        'lastName',
        'document',
        'email',
        'phone',
        'zipCode',
        'state',
        'city',
        'street',
        'district',
        'number',
        'complement',
      ]
      if (isCompany) firstStepFields.push('corporateName')
      if (!isCompany) firstStepFields.push('gender')

      // Tocar todos os campos para ativar a validação
      firstStepFields.forEach(field => {
        formik.setFieldTouched(field, true, false)
      })

      // Validar campos
      const errors = await formik.validateForm()
      const hasErrors = firstStepFields.some(field => Boolean(errors[field as keyof typeof formik.values]))

      if (!hasErrors) {
        setCurrentStep(2)
      }
    } else if (currentStep === 2) {
      // Validar campos de senha
      await formik.setFieldTouched('password', true, false)
      await formik.setFieldTouched('confirmPassword', true, false)
      const errors = await formik.validateForm()

      if (!errors.password && !errors.confirmPassword) {
        setCurrentStep(3)
      }
    } else if (currentStep === 3) {
      setCurrentStep(4)
    }
  }

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1)
    } else if (currentStep === 3) {
      setCurrentStep(2)
    } else if (currentStep === 4) {
      setCurrentStep(3)
    } else {
      history.goBack()
    }
  }

  return (
    <S.Container>
      <UnauthenticatedTemplate image={TemplateImage}>
        <S.Wrapper>
          <Hidden mobile>
            <S.Logo src={Logo} />
          </Hidden>
          <S.ContentForm>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "left" }} onClick={handleBack}>
              <IconBack />
              <Title colorText={'white'}>Voltar</Title>
            </div>
            <S.TextPassword>Cadastre-se</S.TextPassword>
            {inviteInfo && (
              <Paragraph variant="large" type="bold" style={{ marginBottom: '16px' }}>
                {inviteInfo.inviterName} convidou você para ser produtor(a) no evento {inviteInfo.eventName}.
              </Paragraph>
            )}
            <S.Form onSubmit={(e) => {
              // Prevenir submissão do form se não estiver no último passo
              if (currentStep !== 4) {
                e.preventDefault()
                return
              }
              formik.handleSubmit(e)
            }}>
              {currentStep === 1 ? (
                <>
                  <S.LegalPerson>
                    <RadioButton
                      name="isCompany"
                      onChange={() => handlePersonTypeChange(false)}
                      checked={!isCompany}
                    />
                    <Paragraph variant="large" type="bold">
                      Sou pessoa física
                    </Paragraph>
                  </S.LegalPerson>
                  <S.LegalPerson>
                    <RadioButton
                      name="isCompany"
                      onChange={() => handlePersonTypeChange(true)}
                      checked={isCompany}
                    />
                    <Paragraph variant="large" type="bold">
                      Sou pessoa jurídica
                    </Paragraph>
                  </S.LegalPerson>

                  <S.TextObrigatory>*Obrigatório</S.TextObrigatory>

                  <S.GroupFormDataCreator>
                    <Input
                      name="name"
                      label="*Nome"
                      placeholder="Digite seu nome completo"
                      value={formik.values.name}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      texterror={formik.errors.name}
                      onChange={formik.handleChange}
                      fullWidth
                    />
                    <Input
                      name="lastName"
                      label="*Sobrenome"
                      placeholder="Digite seu sobrenome"
                      value={formik.values.lastName}
                      error={
                        formik.touched.lastName && Boolean(formik.errors.lastName)
                      }
                      texterror={formik.errors.lastName}
                      onChange={formik.handleChange}
                      fullWidth
                    />

                    {isCompany ? (
                      <Input
                        name="corporateName"
                        label="*Razão Social"
                        placeholder="Digite a razão social de sua empresa"
                        value={formik.values.corporateName}
                        error={
                          formik.touched.corporateName &&
                          Boolean(formik.errors.corporateName)
                        }
                        texterror={formik.errors.corporateName}
                        onChange={formik.handleChange}
                        fullWidth
                      />
                    ) : undefined}

                    <InputMask
                      key={isCompany ? 'cnpj-document' : 'cpf-document'}
                      name="document"
                      mask={isCompany ? '99.999.999/9999-99' : '999.999.999-99'}
                      fullWidth
                      label={isCompany ? '*CNPJ' : '*CPF'}
                      placeholder={
                        isCompany ? '00.000.000/0000-00' : '000.000.000-00'
                      }
                      onChange={formik.handleChange}
                      value={formik.values.document}
                      error={
                        formik.touched.document && Boolean(formik.errors.document)
                      }
                      texterror={formik.errors.document}
                    />

                    <Input
                      name="email"
                      label="*Email"
                      placeholder="Digite seu email"
                      value={formik.values.email}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      texterror={formik.errors.email}
                      onChange={formik.handleChange}
                      disabled={!!inviteToken && !!inviteInfo}
                      fullWidth
                    />

                    <InputMask
                      name="phone"
                      mask="(99) 9 9999-9999"
                      label="*Telefone"
                      placeholder="(00) 9 0000-0000"
                      value={formik.values.phone}
                      error={formik.touched.phone && Boolean(formik.errors.phone)}
                      texterror={formik.errors.phone}
                      onChange={formik.handleChange}
                      fullWidth
                    />

                    <InputMask
                      mask="99999-999"
                      name="zipCode"
                      label="*CEP"
                      placeholder="Digite seu CEP"
                      value={formik.values.zipCode}
                      error={
                        formik.touched.zipCode && Boolean(formik.errors.zipCode)
                      }
                      texterror={formik.errors.zipCode}
                      onChange={(e) => {
                        formik.handleChange(e)
                        handleCep(e.target.value)
                      }}
                      fullWidth
                    />

                    <div style={{ marginBottom: '16px' }}>
                      <S.Label>*Estado</S.Label>
                      <ReactSelect
                        options={states}
                        onChange={handleStateChange}
                        value={stateSelectValue}
                        noOptionsMessage={noOptionsState}
                        placeholder="Estado"
                        styles={S.customStyles}
                      />
                      <S.Error>
                        {formik.touched.state && formik.errors.state
                          ? formik.errors.state
                          : null}
                      </S.Error>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <S.Label>*Cidade</S.Label>
                      <ReactSelect
                        options={cities}
                        onChange={handleCityChange}
                        value={citySelectValue}
                        noOptionsMessage={cityNoOptionsMessage}
                        placeholder="Cidade"
                        styles={S.customStyles}
                      />
                      <S.Error>
                        {formik.touched.city && Boolean(formik.errors.city)
                          ? formik.errors.city
                          : null}
                      </S.Error>
                    </div>

                    <Input
                      name="street"
                      label="*Endereço"
                      placeholder="Digite seu endereço"
                      value={formik.values.street}
                      error={formik.touched.street && Boolean(formik.errors.street)}
                      texterror={formik.errors.street}
                      onChange={formik.handleChange}
                      fullWidth
                    />
                    <Input
                      name="district"
                      label="*Bairro"
                      placeholder="Digite seu bairro"
                      value={formik.values.district}
                      error={
                        formik.touched.district && Boolean(formik.errors.district)
                      }
                      texterror={formik.errors.district}
                      onChange={formik.handleChange}
                      fullWidth
                    />
                    <Input
                      name="number"
                      label="*Número"
                      type="number"
                      placeholder="Digite seu número"
                      value={formik.values.number}
                      error={formik.touched.number && Boolean(formik.errors.number)}
                      texterror={formik.errors.number}
                      onChange={formik.handleChange}
                      fullWidth
                    />
                    <Input
                      name="complement"
                      label="Complemento"
                      placeholder="Digite o complemento"
                      value={formik.values.complement}
                      error={formik.touched.complement && Boolean(formik.errors.complement)}
                      texterror={formik.errors.complement}
                      onChange={formik.handleChange}
                      fullWidth
                    />

                    {!isCompany && (
                      <div style={{ marginBottom: '16px' }}>
                        <S.Label>*Gênero</S.Label>
                        <ReactSelect
                          options={GENDER_OPTIONS}
                          onChange={handleGenderChange}
                          value={genderSelectValue}
                          noOptionsMessage={noOptionsGender}
                          placeholder="Selecione seu gênero"
                          styles={S.customStyles}
                        />
                        <S.Error>
                          {formik.touched.gender && Boolean(formik.errors.gender)
                            ? formik.errors.gender
                            : null}
                        </S.Error>
                      </div>
                    )}
                  </S.GroupFormDataCreator>
                  <Button
                    color="primary"
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={handleNextStep}
                    type="button">
                    Avançar
                  </Button>
                </>
              ) : currentStep === 2 ? (
                <>
                  <S.Form onSubmit={(e) => e.preventDefault()}>
                    <Input
                      label="*Senha"
                      name="password"
                      placeholder="Senha"
                      type="password"
                      error={
                        formik.touched.password && Boolean(formik.errors.password)
                      }
                      texterror={formik.errors.password}
                      onChange={formik.handleChange}
                      fullWidth
                      value={formik.values.password}
                    />
                    <Input
                      label="*Confirmar Senha"
                      name="confirmPassword"
                      placeholder="Confirmar Senha"
                      type="password"
                      error={
                        formik.touched.confirmPassword &&
                        Boolean(formik.errors.confirmPassword)
                      }
                      texterror={formik.errors.confirmPassword}
                      onChange={formik.handleChange}
                      value={formik.values.confirmPassword}
                      fullWidth
                    />
                    <S.PasswordRules>
                      <S.PasswordRule>* Pelo menos 8 caracteres</S.PasswordRule>
                      <S.PasswordRule>* No mínimo 1 letra e 1 número</S.PasswordRule>
                    </S.PasswordRules>
                  </S.Form>
                  <Button
                    color="primary"
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={handleNextStep}
                    type="button">
                    Avançar
                  </Button>
                </>
              ) : currentStep === 3 ? (
                <>
                  <S.ProfileSection>
                    <S.ProfileImageContainer>
                      {profileImagePreview ? (
                        <S.ProfileImage src={profileImagePreview} alt="Foto de perfil" />
                      ) : (
                        <S.ProfilePlaceholder>
                          <S.UserIcon />
                        </S.ProfilePlaceholder>
                      )}
                      <S.ImageInput
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        id="profile-image"
                      />
                      <S.AddImageButton htmlFor="profile-image">
                        <S.PlusIcon />
                      </S.AddImageButton>
                    </S.ProfileImageContainer>
                    <S.Label>Foto de perfil</S.Label>
                  </S.ProfileSection>

                  <div>
                    <S.Label>Descrição</S.Label>
                    <S.StyledTextArea
                      name="description"
                      placeholder="Escreva sua descrição aqui"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                    />
                  </div>

                  <S.SocialNetworksSection>
                    <S.Label>Redes</S.Label>
                    {socialNetworks.map((network, index) => (
                      <S.SocialNetworkRow key={index}>
                        <ReactSelect
                          className="select-container"
                          options={SOCIAL_NETWORK_OPTIONS}
                          value={SOCIAL_NETWORK_OPTIONS.find(opt => opt.value === network.type) ?? null}
                          onChange={(e) => handleSocialNetworkChange(index, 'type', e!.value)}
                          styles={S.customStyles}
                        />
                        <Input
                          placeholder="www.instagram.com/loremipsum"
                          value={network.url}
                          onChange={(e) => handleSocialNetworkChange(index, 'url', e.target.value)}
                          fullWidth
                        />
                      </S.SocialNetworkRow>
                    ))}
                    <S.AddButton onClick={handleAddSocialNetwork} type="button">
                      <S.PlusIcon />
                    </S.AddButton>
                  </S.SocialNetworksSection>

                  <Button
                    color="primary"
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={handleNextStep}
                    type="button">
                    Avançar
                  </Button>
                </>
              ) : (
                <>
                  <S.InterestsSection>
                    <div>
                      <S.InterestsTitle>Boas-vindas!</S.InterestsTitle>
                      <S.InterestsSubtitle>
                        Selecione as categorias mais alinhadas com seu conteúdo:
                      </S.InterestsSubtitle>
                    </div>

                    <S.InterestsGrid>
                      {interests.map((interest) => (
                        <S.InterestButton
                          key={interest.id}
                          selected={selectedInterests.includes(interest.id)}
                          onClick={() => handleInterestToggle(interest.id)}
                          type="button"
                        >
                          {interest.name}
                        </S.InterestButton>
                      ))}
                    </S.InterestsGrid>
                  </S.InterestsSection>

                  <Button
                    color="primary"
                    fullWidth
                    size="large"
                    variant="contained"
                    type="submit">
                    Cadastrar
                  </Button>
                </>
              )}
            </S.Form>
          </S.ContentForm>
        </S.Wrapper>
      </UnauthenticatedTemplate>
    </S.Container>
  )
}
